import { useRef, useState, useEffect } from 'react';
import Highcharts, { Options } from 'highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { useGetNetPositionHeatMap } from 'query/reporting';
import { StandardReportInfo } from 'types/reporting';
import { colors } from 'theme/colors';
import { merge } from 'lodash';
import psVHighchartsTheme from 'theme/highcharts-theme';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Box } from '@chakra-ui/react';

const chartOptions = (intl: IntlShape): Options =>
    merge(
        {},
        psVHighchartsTheme as Options,
        {
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen', 'viewData'],
                        symbol: 'menu',
                    },
                },
                csv: {
                    columnHeaderFormatter: function (_: any, key: any) {
                        // update the 'y' column header to 'date'
                        if (key === 'y') {
                            return 'date';
                        }
                        // Empty keys will shift the columns to the left
                        return key ? key : " "
                    },
                },
            },
            chart: {
                type: 'heatmap',
                marginTop: 80,
                marginBottom: 80,
                plotBorderWidth: 1,
                panning: {
                    enabled: true,
                    type: 'y',
                },
                panKey: 'alt',
                resetZoomButton: {
                    theme: {
                        fill: 'var(--chakra-colors-background-quaternary)',
                        stroke: 'transparent',
                        r: '4px',
                        style: {
                            color: 'var(--chakra-colors-text-quaternary)',
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            fontWeight: '500',
                        },
                        states: {
                            hover: {
                                fill: 'var(--chakra-colors-background-quinary)',
                            },
                        },
                    },
                },
            },
            title: {
                text: intl.formatMessage({
                    id: 'reporting_portfolio_energy_revenue_portfolio_month_hour_net_position_title',
                }),
                style: {
                    fontSize: '1.2em',
                },
                margin: 40,
            },
            yAxis: {
                reversed: true,
                title: {
                    text: null,
                },
                labels: {
                    align: 'left',
                },
                offset: 0,
                lineWidth: 0, 
            },
            legend: {
                enabled: true,
                layout: 'vertical',
                align: 'right',
                verticalAlign: 'middle',
                itemMarginTop: 10,
                itemMarginBottom: 10,
                itemStyle: {
                    fontSize: '16px',
                },
                margin: 0,
                y: 25,
                symbolHeight: 280,
            },
            xAxis: {
                labels: {
                    style: {
                        fontSize: '14px',
                    },
                    align: 'center',
                },
                offset: 0,
                lineWidth: 0, 
            },
            colorAxis: {
                min: 0,
                reversed: false,
                nullColor: '#FFFFFF',
            },
            tooltip: {
                pointFormatter: function () {
                    const value =
                        this.value?.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }) ?? '0.00';
                    return `<b>${value} MWh</b>`;
                },
            },
        } as Options
    );

interface MonthHourHeatMapChartProps {
    info: StandardReportInfo;
    studyId: number;
    itemId: number;
    year: string;
}

export const MonthHourHeatMapChart = ({ info, studyId, itemId, year }: MonthHourHeatMapChartProps) => {
    const intl = useIntl();
    const ref = useRef<HighchartsReactRefObject>(null);
    const [options, setOptions] = useState<Options>(chartOptions(intl));

    const { data, isFetching, isError } = useGetNetPositionHeatMap({
        studyId: studyId,
        itemId: itemId,
        year: year,
        reportType: 'PortfolioEnergyAndRev',
    });

    useEffect(() => {
        if (ref.current) {
            isFetching ? ref.current.chart.showLoading() : ref.current.chart.hideLoading();
        }
    }, [ref, isFetching]);

    useEffect(() => {
        if (ref.current && data && info.heLabels) {
            ref.current.chart.hideData();

            // Create a complete grid with all x/y combinations
            const numMonths = 12;
            const numHours = info.heLabels.length;

            // Create a map of existing data points for quick lookup
            const dataMap = new Map<string, number>();
            data.forEach((point) => {
                const key = `${point[0]},${point[1]}`;
                dataMap.set(key, point[2]);
            });

            // Generate complete grid data
            const completeData: { x: number; y: number; value: number | null }[] = [];
            for (let y = 0; y < numMonths; y++) {
                for (let x = 0; x < numHours; x++) {
                    const key = `${x},${y}`;
                    const value = dataMap.get(key);
                    completeData.push({ x, y, value: value ?? null });
                }
            }

            const updatedSeries: Highcharts.SeriesOptionsType[] = [
                {
                    type: 'heatmap',
                    name: intl.formatMessage({
                        id: 'reporting_portfolio_energy_revenue_portfolio_month_hour_net_position_title',
                    }),
                    data: completeData,
                    nullColor: '#FFFFFF',
                    dataLabels: {
                        enabled: true,
                        color: 'contrast',
                        formatter: function () {
                            return this.point.value == null ? '-' : this.point.value;
                        },
                        style: {
                            textOutline: 'none',
                            fontWeight: 'normal',
                        },
                    },
                },
            ];

            // Calculate heatmap color axis range (only from non-null values)
            const nonNullValues = data.map((point) => point[2]).filter((val) => val != null);

            const min = Math.min(...nonNullValues);
            const max = Math.max(...nonNullValues);

            const actualMin = Math.min(min, 0);
            const actualMax = Math.max(max, 0);

            const totalRange = Math.abs(actualMin) + Math.abs(actualMax);
            const zeroPosition = totalRange === 0 ? 0.5 : Math.abs(actualMin) / totalRange;

            setOptions({
                series: updatedSeries,
                colorAxis: {
                    min: actualMin,
                    max: actualMax,
                    stops: [
                        [0, colors.red[500]],
                        [zeroPosition - 0.01, '#FFFFFF'],
                        [zeroPosition, '#FFFFFF'],
                        [zeroPosition + 0.01, '#FFFFFF'],
                        [1, colors.green[500]],
                    ],
                } as Highcharts.ColorAxisOptions,
                yAxis: {
                    categories: Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`),
                },
                xAxis: {
                    categories: info.heLabels || [],
                    opposite: true,
                    offset: 0,
                    lineWidth: 0
                },
            });
        }
    }, [ref, data, year, info.heLabels, info.startYear, info.endYear, intl]);

    if (isError) {
        return <FormattedMessage id="common_generic_loading_error" />;
    }

    return (
        <Box mr={2} minW={1100}>
            <HighchartsReact
                ref={ref}
                options={options}
                highcharts={Highcharts}
                containerProps={{ style: { width: '100%', height: '60vh', minHeight: '400px' } }}
            />
        </Box>
    );
};
